import React, { Component } from 'react';
import { connect } from 'react-redux';
import { formValueSelector, reduxForm } from 'redux-form';
import flatten from 'flat';
import { get, isEmpty, set } from 'lodash';
import pluralize from 'pluralize';
import { bindActionCreators } from 'redux';
import MdRestore from 'react-icons/lib/md/restore';
import { Alert, Col, Row } from 'react-bootstrap';
import { actions } from '../../../../redux/productActions';
import getIncludedResource from '../../../../../../helpers/getIncludedResource';
import validate from '../../ReduxForm/validate';
import SidebarSection from '../../../Sidebar/SidebarSection/SidebarSection';
import Broker from '../../../Sidebar/Broker/Broker';
import AdminFeeAdjust from '../../../Sidebar/AdminFeeAdjust/AdminFeeAdjust';
import PremiumAdjust from '../../../Sidebar/PremiumAdjust/PremiumAdjust';
import getCoverDates from '../../../../../../helpers/getCoverDates';
import SchemeSummary from '../../../Sidebar/SchemeSummary/SchemeSummary';
import MtaPremiumSummary from '../../../Sidebar/MtaPremiumSummary/MtaPremiumSummary';
import QuoteActions from '../../../Sidebar/QuoteActions/QuoteActions';
import MtaPremiumOverride from '../../../Sidebar/MtaPremiumOverride/MtaPremiumOverride';
import TravellerInformationModal from '../../../TravellerInformationModal/TravellerInformationModal';
import { DotLoader } from '../../../../../../common/components/Loader/Loader';
import './SideBar.scss';

const MEDICAL_TAB = 3;

const TRIP_TYPE_LABEL = {
  annual: 'Annual Multi Trip',
  single: ' SingleTrip',
};
const TravellerInfo = ({ group, adults, children, isRescreenRequired }) => {
  if (!group) {
    return null;
  }
  const numAdults = adults.length;
  const adultDescription = numAdults
    ? `${numAdults} ${pluralize('adult', numAdults)}`
    : '';
  const numChildren = children.length;
  const childrenDescription = numChildren
    ? `${numChildren} ${pluralize('child', numChildren)}`
    : '';
  return (
    <SidebarSection
      label={'Travellers:'}
      info={
        <>
          {group}:{adultDescription} {childrenDescription} &nbsp;
          <TravellerInformationModal adults={adults} children={children} />
          {isRescreenRequired && (
            <Alert bsStyle={'danger'}>IMPORTANT: Re-screen is required</Alert>
          )}
        </>
      }
    />
  );
};

const AdditionalOptionInfo = ({ options, individualOptions }) => {
  if (!options) {
    return null;
  }

  const selectedOptions = Object.keys(options).reduce((carry, optionKey) => {
    const value = options[optionKey];
    if (optionKey === 'hazardous_activities') {
      carry.push(`Hazardous Activities - Category ${value}`);
      return carry;
    }
    if (value) {
      carry.push(optionKey);
    }
    return carry;
  }, []);

  const allSelectedOptions = individualOptions
    ? individualOptions.reduce((carry, option) => {
        if (carry.indexOf(option) < 0) {
          carry.push(option);
        }
        return carry;
      }, selectedOptions)
    : [];
  if (!allSelectedOptions.length) {
    return null;
  }

  return (
    <SidebarSection
      label={'Additional cover:'}
      info={
        <>
          {allSelectedOptions.map((option) => (
            <div>{option.replace(/_/g, ' ')}</div>
          ))}
        </>
      }
    />
  );
};

export class Sidebar extends Component {
  getSelectedSchemes() {
    const { formValues, product } = this.props;
    let selectedSchemes = get(formValues, 'data.relationships.schemes.data', []);
    const quoteLimit = 5;

    if (isEmpty(product.quotes)) {
      return [];
    }
    if (isEmpty(selectedSchemes)) {
      // if no schemes are selected use top 5 schemes

      const validQuotes = Object.values(product.quotes)
        .filter((quote) => {
          const matchSchemeType =
            quote.meta.scheme?.scheme_type ===
            formValues?.data?.attributes?.metadata?.scheme_type;
          return matchSchemeType && quote?.meta?.premium?.gross > 0;
        })
        .sort((quoteA, quoteB) => {
          const quoteAGross = get(quoteA, 'meta.premium.gross', 0);
          const quoteBGross = get(quoteB, 'meta.premium.gross', 0);
          return quoteAGross - quoteBGross;
        });

      const topQuotes = validQuotes.slice(0, quoteLimit);

      selectedSchemes = topQuotes.map((quote) => {
        return get(quote, 'meta.scheme');
      });
    }

    return selectedSchemes;
  }

  handleAdminFeeApplyButton = () => {
    // Handle Admin Fee Apply Button
    const {
      formValues,
      dispatch,
      handleSubmit,
      calculateQuotes,
      product,
      valid,
    } = this.props;
    const selectedSchemes = get(formValues, 'data.relationships.schemes.data', []);

    if (!valid) {
      return;
    }

    if (selectedSchemes && selectedSchemes.length === 1) {
      const newSchemeSelected = get(product.quotes, selectedSchemes[0].id);
      Promise.resolve(this.props.singleCalculation(newSchemeSelected)).then(() => {
        return dispatch(handleSubmit(calculateQuotes));
      });
    } else {
      return dispatch(handleSubmit(calculateQuotes));
    }
  };

  saveAndExit = () => {
    const { currentProduct, application, mta, productActions } = this.props;
    const selectedSchemes = this.getSelectedSchemes();
    set(application, 'data.relationships.schemes.data', selectedSchemes);
    const calculate = selectedSchemes.length > 0;
    if (application.data.id && !mta) {
      return productActions.updateApplicationAndRedirect(
        application,
        currentProduct,
        application.data.id,
        calculate,
      );
    }
    return productActions.saveApplicationAndRedirect(
      application,
      currentProduct,
      false,
      calculate,
    );
  };
  saveEmailAndExit = () => {
    const { currentProduct, application, productActions } = this.props;
    const selectedSchemes = this.getSelectedSchemes();
    set(application, 'data.relationships.schemes.data', selectedSchemes);
    set(application, 'data.attributes.metadata.generate_documents', true);
    set(application, 'meta.customer.create_account', true);
    const calculate = selectedSchemes.length > 0;
    if (application.data.id) {
      return productActions.updateApplicationEmailAndRedirect(
        application,
        currentProduct,
        application.data.id,
        calculate,
      );
    }
    return productActions.saveApplicationAndRedirect(
      application,
      currentProduct,
      true,
      calculate,
    );
  };

  render() {
    const {
      currentProduct,
      formValues,
      product,
      resource,
      error,
      mta,
      submitFailed,
      productName,
      isRecalculatingScore,
      isRescreenRequired,
    } = this.props;
    const formErrors = error && error instanceof Object ? flatten(error) : false;
    const displayedErrors = [];
    const currentTab = get(formValues, 'data.current_tab');
    let revertUpsellButton = null;

    // Scheme Selected
    const selectedSchemes = get(formValues, 'data.relationships.schemes.data', []);
    const hasPremiumIndication =
      selectedSchemes.length === 1 && get(product, 'premiumIndication.meta', false);
    const quoteFromSelectedScheme =
      selectedSchemes.length === 1
        ? get(product, `quotes.${selectedSchemes[0].id}`)
        : null;
    const selectedPremium = hasPremiumIndication
      ? get(product, 'premiumIndication', false)
      : quoteFromSelectedScheme;

    const tripType = get(formValues, 'data.attributes.metadata.scheme_type');

    if (tripType === 'annual' && this.props.isUpsellDirty) {
      revertUpsellButton = (
        <span
          style={{ color: '#ce263a', cursor: 'pointer' }}
          onClick={this.props.revertUpsell}
        >
          {' '}
          <MdRestore size={20} />{' '}
        </span>
      );
    }

    // Calculate the trip number of days

    // const originalSchemes = get(resource, 'data.relationships.schemes.data')
    // const schemeId = get(head(originalSchemes), 'id')

    const isAnnual = tripType === 'annual';
    const region = get(
      product,
      'premiumIndication.meta.information.region',
      get(formValues, 'data.attributes.metadata.region', ''),
    );
    const destinations = get(
      formValues,
      'data.attributes.metadata.destinations',
      [],
    ).join(', ');
    const maxTripDuration =
      isAnnual && get(formValues, 'data.attributes.metadata.max_trip_duration')
        ? `${get(formValues, 'data.attributes.metadata.max_trip_duration')} Days`
        : null;

    return (
      <div>
        <div className="calculate-quote-sidebar">
          <div className={'sidebar-sticky-container'}>
            <Row className="sticky-inner-container sticky-sidebar">
              <Col xs={12}>
                <h4 className="quote-summary-title">
                  <span>
                    {' '}
                    Quote Summary {mta && <span className="mta-logo-sidebar">MTA</span>}
                  </span>
                </h4>
              </Col>
              <SidebarSection label={'Product'} info={productName} />

              <SidebarSection
                label={'Policy number'}
                info={resource.data.attributes.policy_number}
              />
              {this.props.isMultipleSchemeSelected &&
                !this.props.getPremium &&
                !this.props.getPremium !== 0 && (
                  <div className="col-xs-12 quote-divider">
                    <div className="error-box">
                      <span>Please select 1 scheme to proceed</span>
                    </div>
                  </div>
                )}
              {!this.props.isSignpostAcknowledged && (
                <div className="col-xs-12 quote-divider">
                  <div className="error-box">
                    <span>
                      Please confirm the signpost text has been read and acknowledged{' '}
                    </span>
                  </div>
                </div>
              )}
              {submitFailed && error && formErrors && (
                <div className="col-xs-12 quote-divider">
                  <div className="error-box">
                    <h4>Attention required</h4>
                    <ul>
                      {Object.keys(formErrors).map((key, i) => {
                        if (displayedErrors.indexOf(formErrors[key]) === -1) {
                          displayedErrors.push(formErrors[key]);
                          return <li key={i}>{formErrors[key]}</li>;
                        }
                      })}
                    </ul>
                  </div>
                </div>
              )}

              <Broker
                broker={getIncludedResource(resource.data, resource.included, 'broker')}
                source={resource.data.relationships.source}
              />
              <SidebarSection
                label={'Product:'}
                info={get(currentProduct, 'data.attributes.name')}
              />
              <SidebarSection
                label={'Type of cover:'}
                info={
                  tripType ? (
                    <>
                      {TRIP_TYPE_LABEL[tripType]}
                      <span className="pull-right">{revertUpsellButton}</span>
                    </>
                  ) : null
                }
              />
              <SidebarSection
                label={'Dates of cover:'}
                info={getCoverDates(
                  get(formValues, 'data.attributes.metadata.start_date'),
                  get(formValues, 'data.attributes.metadata.end_date'),
                  isAnnual,
                )}
              />

              <SidebarSection label={'Destination:'} info={isAnnual ? region : null} />
              <SidebarSection
                label={'Destinations:'}
                info={!isAnnual ? destinations : null}
              />

              <SidebarSection label={'Max trip duration:'} info={maxTripDuration} />

              <TravellerInfo
                group={formValues?.data?.attributes?.metadata?.traveller_group}
                adults={formValues?.data?.attributes?.metadata?.adults}
                children={formValues?.data?.attributes?.metadata?.children}
                isRescreenRequired={isRescreenRequired}
              />

              <AdditionalOptionInfo
                options={get(formValues, 'data.attributes.metadata.options')}
                individualOptions={get(
                  formValues,
                  'data.attributes.metadata.storage.individualOptionsSelected',
                )}
              />
              {isRecalculatingScore && (
                <Col xs={12} className="sidebar-section--quote-divider">
                  <div className="error-box">
                    Recalculating Medical Score
                    <DotLoader />
                  </div>
                </Col>
              )}

              {selectedPremium && selectedPremium.meta && (
                <SchemeSummary
                  selection={selectedPremium.meta}
                  isMta={mta}
                  isSaving={product.isSaving}
                  isUpdating={product.isUpdatingPremiumIndication}
                />
              )}

              {!!product.quotes && !selectedPremium && selectedSchemes.length > 0 && (
                <>
                  {selectedSchemes.map((scheme) => {
                    return (
                      <SchemeSummary
                        selection={get(product.quotes, `${scheme.id}.meta`)}
                        isMta={mta}
                        isSaving={product.isSaving}
                      />
                    );
                  })}
                </>
              )}

              <MtaPremiumSummary
                premium={selectedPremium?.meta?.mta?.new_premium ?? undefined}
                scheme={selectedPremium?.meta.scheme}
                benefits={selectedPremium?.meta.benefits}
                taxRate={selectedPremium?.meta.rates.tax_rate}
                isMta={mta}
              />

              <MtaPremiumSummary
                premium={selectedPremium?.meta?.mta?.new_premium}
                scheme={selectedPremium?.meta.scheme}
                benefits={selectedPremium?.meta.benefits}
                taxRate={selectedPremium?.meta.rates.tax_rate}
                isMta={mta}
                payable={selectedPremium?.meta}
              />

              <MtaPremiumOverride
                hasPremiums={!!selectedSchemes.length}
                selectedPremium={selectedPremium ? selectedPremium.meta : null}
                isMta={mta}
                handleGrossOverride={this.handleAdminFeeApplyButton}
              />

              <QuoteActions
                showSave={currentTab >= MEDICAL_TAB}
                showEmail={
                  currentTab >= MEDICAL_TAB &&
                  !mta &&
                  get(formValues, 'data.attributes.metadata.adults[0].email') &&
                  !isEmpty(product.quotes)
                }
                saveAndExit={this.saveAndExit}
                saveEmailAndExit={this.saveEmailAndExit}
                goToNextPage={() => this.props.goToNextPage()}
                isSaving={product.isSaving}
                showNext={currentTab > MEDICAL_TAB && get(selectedPremium, 'meta')}
              />
            </Row>
            { selectedPremium &&

              <Row className={'adjustments-container col-xs-12'}>
                <PremiumAdjust
                  selectedScheme={selectedPremium ? selectedPremium.meta : null}
                  isMta={mta}
                  isCalculating={product.isCalculating}
                  handleCommissionAdjustments={this.props.handleCommissionAdjustments}
                />
                <AdminFeeAdjust
                  selectedScheme={selectedPremium ? selectedPremium.meta : null}
                  isPremiumAdjusting={product.isPremiumAdjusting}
                  applyAdminFeeOverride={this.handleAdminFeeApplyButton}
                />
              </Row>
            }

          </div>
        </div>
      </div>
    );
  }
}

const FORM_NAME = 'LeisureTravelForm';
const form = reduxForm({ form: FORM_NAME, validate })(Sidebar);

const selector = formValueSelector(FORM_NAME);
const mapStateToProps = (state, ownProps) => {
  const values = selector(
    state,
    'data.id',
    'data.current_tab',
    'data.relationships',
    'data.attributes.metadata',
  );

  return {
    auth: state.auth,
    initialValues: ownProps.resource,
    form: 'LeisureTravelForm',
    formValues: values,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
    product: state.product,
  };
};
const mapDispatchToProps = (dispatch) => ({
  productActions: bindActionCreators(actions, dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(form);
